import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CoolifyServerDto,
  CreateServiceInput,
  PlanFeatures,
  PlanLimits,
  ResourceDto,
  ServiceBlueprintCatalogDto,
  ServiceBlueprintDto,
  ServiceCatalogDto,
  ServiceTemplateDto,
  SubscriptionDto,
} from '@cpm/shared';
import {
  ArrowLeft,
  Boxes,
  ExternalLink,
  FileCode2,
  RefreshCw,
  Rocket,
  Search,
  WandSparkles,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../api/client.js';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorNote,
  Input,
  Label,
  PageHeader,
  Select,
  Spinner,
  Textarea,
  cx,
} from '../components/ui.js';

interface SubscriptionDetailResponse {
  subscription: SubscriptionDto;
  limits: PlanLimits;
  features: PlanFeatures;
}

interface CreateServiceResponse {
  service: ResourceDto;
  domains: string[];
  warning: string | null;
}

function TemplateCard({
  template,
  selected,
  onSelect,
}: {
  template: ServiceTemplateDto;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cx(
        'flex items-start gap-3 rounded-lg border p-3 text-left transition-colors',
        selected
          ? 'border-brand-600 bg-brand-50 dark:border-brand-400 dark:bg-brand-500/10'
          : 'border-slate-200 hover:border-slate-300 dark:border-slate-700 dark:hover:border-slate-600',
      )}
    >
      {template.logoUrl ? (
        <img
          src={template.logoUrl}
          alt=""
          loading="lazy"
          className="mt-0.5 size-8 shrink-0 rounded object-contain"
          onError={(e) => {
            (e.target as HTMLImageElement).style.visibility = 'hidden';
          }}
        />
      ) : (
        <Boxes className="mt-0.5 size-8 shrink-0 text-slate-300 dark:text-slate-600" />
      )}
      <span className="min-w-0">
        <span className="flex items-center gap-2">
          <span className="truncate text-sm font-medium">{template.name}</span>
          <Badge>{template.category}</Badge>
        </span>
        <span className="mt-0.5 line-clamp-2 block text-xs text-slate-500 dark:text-slate-400">
          {template.slogan}
        </span>
      </span>
    </button>
  );
}

function BlueprintCard({
  blueprint,
  selected,
  onSelect,
}: {
  blueprint: ServiceBlueprintDto;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cx(
        'rounded-lg border p-3 text-left transition-colors',
        selected
          ? 'border-brand-600 bg-brand-50 dark:border-brand-400 dark:bg-brand-500/10'
          : 'border-slate-200 hover:border-slate-300 dark:border-slate-700 dark:hover:border-slate-600',
      )}
    >
      <span className="flex items-center gap-2">
        <WandSparkles className="size-4 text-brand-600 dark:text-brand-400" />
        <span className="font-medium">{blueprint.name}</span>
        <Badge tone={blueprint.available ? 'green' : 'yellow'}>
          {blueprint.available ? blueprint.kind : 'unavailable'}
        </Badge>
      </span>
      <span className="mt-1 block text-xs text-slate-500 dark:text-slate-400">
        {blueprint.summary}
      </span>
      {!blueprint.available && (
        <span className="mt-1 block text-xs text-amber-700 dark:text-amber-400">
          {blueprint.unavailableReason}
        </span>
      )}
    </button>
  );
}

export function ServiceCreatePage() {
  const { id: subscriptionId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [mode, setMode] = useState<'blueprint' | 'template' | 'compose'>('blueprint');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [selectedBlueprintId, setSelectedBlueprintId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [serverUuid, setServerUuid] = useState('');
  const [compose, setCompose] = useState('');
  const [instantDeploy, setInstantDeploy] = useState(true);

  const detail = useQuery({
    queryKey: ['subscriptions', subscriptionId],
    queryFn: () => api.get<SubscriptionDetailResponse>(`/api/subscriptions/${subscriptionId}`),
    enabled: Boolean(subscriptionId),
  });
  const servers = useQuery({
    queryKey: ['coolify', 'servers'],
    queryFn: () => api.get<CoolifyServerDto[]>('/api/coolify/servers'),
  });
  const catalog = useQuery({
    queryKey: ['services', 'catalog', search, category],
    queryFn: () => {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (category) params.set('category', category);
      const qs = params.toString();
      return api.get<ServiceCatalogDto>(`/api/services/catalog${qs ? `?${qs}` : ''}`);
    },
    enabled: mode === 'template',
    staleTime: 60_000,
  });
  const blueprints = useQuery({
    queryKey: ['services', 'blueprints'],
    queryFn: () => api.get<ServiceBlueprintCatalogDto>('/api/services/blueprints'),
    enabled: mode === 'blueprint',
  });

  const refresh = useMutation({
    mutationFn: () => api.post('/api/services/catalog/refresh'),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['services', 'catalog'] }),
  });

  const create = useMutation({
    mutationFn: (input: CreateServiceInput) =>
      api.post<CreateServiceResponse>('/api/services/', input),
    onSuccess: (result) => {
      void navigate(`/services/${result.service.id}`);
    },
  });

  if (detail.isPending) return <Spinner />;
  if (detail.error) return <ErrorNote error={detail.error} />;
  const { subscription } = detail.data;

  const selectedTemplate =
    catalog.data?.templates.find((t) => t.slug === selectedSlug) ?? null;
  const selectedBlueprint =
    blueprints.data?.blueprints.find((blueprint) => blueprint.id === selectedBlueprintId) ??
    null;
  const blueprintCreatesService =
    selectedBlueprint?.action.type === 'create-service' && selectedBlueprint.available;
  const selectedBlueprintHref =
    selectedBlueprint?.action.type === 'open-wordpress'
      ? selectedBlueprint.action.href
      : selectedBlueprint?.action.type === 'open-application'
        ? selectedBlueprint.action.hrefTemplate.replace(':subscriptionId', subscriptionId!)
        : null;
  const canSubmit =
    name.trim() !== '' &&
    serverUuid !== '' &&
    (mode === 'blueprint'
      ? blueprintCreatesService
      : mode === 'template'
        ? selectedSlug !== null
        : compose.trim() !== '');

  const submit = () => {
    const common = {
      subscriptionId: subscriptionId!,
      serverUuid,
      name,
      instantDeploy,
    };
    create.mutate(
      mode === 'blueprint'
        ? { source: 'blueprint', blueprintId: selectedBlueprintId!, ...common }
        : mode === 'template'
        ? { source: 'template', templateSlug: selectedSlug!, ...common }
        : { source: 'compose', dockerComposeRaw: compose, ...common },
    );
  };

  return (
    <div>
      <Link
        to={`/subscriptions/${subscriptionId}`}
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
      >
        <ArrowLeft className="size-4" /> {subscription.name}
      </Link>
      <PageHeader
        title="New service"
        description={
          <>
            One-click services provisioned into the Coolify project of{' '}
            <strong>{subscription.name}</strong> — plan limits apply.
          </>
        }
        actions={
          <div className="flex rounded-lg bg-slate-100 p-0.5 dark:bg-slate-800">
            {(
              [
                { key: 'blueprint', label: 'Replacement blueprints' },
                { key: 'template', label: 'Template catalog' },
                { key: 'compose', label: 'Custom compose' },
              ] as const
            ).map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => setMode(option.key)}
                className={cx(
                  'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                  mode === option.key
                    ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-700 dark:text-white'
                    : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200',
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        }
      />

      {!subscription.coolifyProjectUuid && (
        <div className="mb-4">
          <ErrorNote error="This subscription has no linked Coolify project — services cannot be provisioned." />
        </div>
      )}

      <div className="space-y-4">
        {mode === 'blueprint' ? (
          <Card>
            <CardHeader
              title="Operator-curated replacement blueprints"
              description={
                blueprints.data?.note ??
                'Supported one-click services, portable application paths and explicit external integrations'
              }
            />
            <div className="space-y-4 px-5 py-4">
              {blueprints.isPending ? (
                <Spinner label="Loading replacement blueprints…" />
              ) : blueprints.error ? (
                <ErrorNote error={blueprints.error} />
              ) : (
                <div className="grid gap-2 md:grid-cols-2">
                  {blueprints.data.blueprints.map((blueprint) => (
                    <BlueprintCard
                      key={blueprint.id}
                      blueprint={blueprint}
                      selected={selectedBlueprintId === blueprint.id}
                      onSelect={() => {
                        setSelectedBlueprintId(blueprint.id);
                        if (!name && blueprint.action.type === 'create-service') {
                          setName(blueprint.id);
                        }
                      }}
                    />
                  ))}
                </div>
              )}

              {selectedBlueprint && (
                <div className="space-y-3 rounded-lg bg-slate-50 px-4 py-3 text-sm dark:bg-slate-800/50">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold">{selectedBlueprint.name}</span>
                    <Badge>{selectedBlueprint.kind}</Badge>
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                      Version source: {selectedBlueprint.versionSource}
                    </span>
                  </div>
                  <div className="grid gap-3 text-xs md:grid-cols-2">
                    <div>
                      <p className="font-medium text-slate-700 dark:text-slate-200">
                        Required resources
                      </p>
                      <ul className="mt-1 list-inside list-disc text-slate-500 dark:text-slate-400">
                        {selectedBlueprint.requiredResources.map((requirement) => (
                          <li key={`${requirement.resource}:${requirement.detail}`}>
                            {requirement.count} {requirement.resource}: {requirement.detail}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <p className="font-medium text-slate-700 dark:text-slate-200">
                        Persistent data and backup
                      </p>
                      <ul className="mt-1 list-inside list-disc text-slate-500 dark:text-slate-400">
                        {selectedBlueprint.persistentData.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                      <p className="mt-1 text-slate-500 dark:text-slate-400">
                        <strong>{selectedBlueprint.backupCoverage.status}:</strong>{' '}
                        {selectedBlueprint.backupCoverage.detail}
                      </p>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    <strong>Maintenance owner:</strong> {selectedBlueprint.maintenanceOwner}
                  </p>
                  {selectedBlueprint.requiredSecrets.length > 0 && (
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      <strong>Secret path:</strong>{' '}
                      {selectedBlueprint.requiredSecrets.join('; ')}. Values are entered only
                      through the owning module&apos;s normal shown-once/encrypted path.
                    </p>
                  )}
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    <p className="font-medium text-slate-700 dark:text-slate-200">
                      Migration notes
                    </p>
                    <ul className="mt-1 list-inside list-disc">
                      {selectedBlueprint.migrationNotes.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    <p className="font-medium text-slate-700 dark:text-slate-200">
                      Export/import checklist
                    </p>
                    <ol className="mt-1 list-inside list-decimal">
                      {selectedBlueprint.exportImportChecklist.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ol>
                  </div>
                  {selectedBlueprintHref && (
                    <Link
                      to={selectedBlueprintHref}
                      className="inline-flex items-center gap-1 font-medium text-brand-600 hover:underline dark:text-brand-400"
                    >
                      Open the owning workflow <ExternalLink className="size-3.5" />
                    </Link>
                  )}
                  {selectedBlueprint.action.type === 'external-checklist' && (
                    <p className="text-xs font-medium text-amber-700 dark:text-amber-400">
                      External integration only — CPM creates no workload or vendor account.
                    </p>
                  )}
                </div>
              )}
            </div>
          </Card>
        ) : mode === 'template' ? (
          <Card>
            <CardHeader
              title="Template catalog"
              description={
                catalog.data ? (
                  <>
                    {catalog.data.total} templates for Coolify {catalog.data.coolifyVersion}
                    {catalog.data.stale && ' (cached copy — refresh failed)'}
                  </>
                ) : (
                  'Templates supported by the connected Coolify instance'
                )
              }
              actions={
                <Button
                  variant="secondary"
                  loading={refresh.isPending}
                  onClick={() => refresh.mutate()}
                  title="Re-fetch the template list from the Coolify repository"
                >
                  <RefreshCw className="size-4" /> Refresh
                </Button>
              }
            />
            <div className="space-y-4 px-5 py-4">
              <div className="flex flex-wrap gap-3">
                <div className="relative min-w-56 flex-1">
                  <Search className="pointer-events-none absolute left-3 top-2.5 size-4 text-slate-400" />
                  <Input
                    className="pl-9"
                    placeholder="Search templates, tags, slogans…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <Select
                  className="w-auto"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  aria-label="Filter by category"
                >
                  <option value="">All categories</option>
                  {(catalog.data?.categories ?? []).map((c) => (
                    <option key={c.name} value={c.name}>
                      {c.name} ({c.count})
                    </option>
                  ))}
                </Select>
              </div>

              {catalog.isPending ? (
                <Spinner label="Loading template catalog…" />
              ) : catalog.error ? (
                <ErrorNote error={catalog.error} />
              ) : catalog.data.templates.length === 0 ? (
                <EmptyState title="No templates match" description="Try a different search or category." />
              ) : (
                <div className="grid max-h-[28rem] gap-2 overflow-y-auto pr-1 sm:grid-cols-2 lg:grid-cols-3">
                  {catalog.data.templates.map((template) => (
                    <TemplateCard
                      key={template.slug}
                      template={template}
                      selected={selectedSlug === template.slug}
                      onSelect={() => {
                        setSelectedSlug(template.slug);
                        if (!name) setName(template.slug);
                      }}
                    />
                  ))}
                </div>
              )}

              {selectedTemplate && (
                <div className="flex flex-wrap items-center gap-2 rounded-lg bg-slate-50 px-4 py-3 text-sm dark:bg-slate-800/50">
                  <span className="font-medium">{selectedTemplate.name}</span>
                  {selectedTemplate.tags.map((tag) => (
                    <Badge key={tag}>{tag}</Badge>
                  ))}
                  {selectedTemplate.documentation && (
                    <a
                      href={selectedTemplate.documentation}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="ml-auto inline-flex items-center gap-1 text-brand-600 hover:underline dark:text-brand-400"
                    >
                      Documentation <ExternalLink className="size-3.5" />
                    </a>
                  )}
                </div>
              )}
            </div>
          </Card>
        ) : (
          <Card>
            <CardHeader
              title="Custom docker-compose"
              description="Staff-only: provision an arbitrary compose stack as a Coolify service"
            />
            <div className="px-5 py-4">
              <Label htmlFor="svc-compose">docker-compose.yml</Label>
              <Textarea
                id="svc-compose"
                rows={12}
                className="font-mono text-xs"
                placeholder={'services:\n  app:\n    image: nginx:alpine\n    ports:\n      - "8080:80"'}
                value={compose}
                onChange={(e) => setCompose(e.target.value)}
              />
            </div>
          </Card>
        )}

        {(mode !== 'blueprint' || blueprintCreatesService) && (
          <Card>
            <CardHeader title="Placement" />
            <div className="space-y-4 px-5 py-4">
              {mode === 'blueprint' &&
                detail.data.features.services_blueprint_install !== true && (
                <div className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 ring-1 ring-inset ring-amber-600/20 dark:bg-amber-500/10 dark:text-amber-300">
                  This plan does not include curated blueprint installation. Enable the
                  matching plan entitlement before creating this replacement.
                </div>
              )}
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="svc-name">Name</Label>
                  <Input
                    id="svc-name"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="svc-server">Server</Label>
                  {servers.isPending ? (
                    <Spinner label="Loading servers…" />
                  ) : servers.error ? (
                    <ErrorNote error={servers.error} />
                  ) : (
                    <Select
                      id="svc-server"
                      value={serverUuid}
                      onChange={(e) => setServerUuid(e.target.value)}
                    >
                      <option value="" disabled>
                        Select a server…
                      </option>
                      {servers.data.map((server) => (
                        <option key={server.uuid} value={server.uuid} disabled={!server.usable}>
                          {server.name} ({server.ip}){server.usable ? '' : ' — not usable'}
                        </option>
                      ))}
                    </Select>
                  )}
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="size-4 rounded border-slate-300 text-brand-600 focus:ring-brand-600"
                  disabled={!detail.data.features.deployments}
                  checked={instantDeploy && detail.data.features.deployments}
                  onChange={(e) => setInstantDeploy(e.target.checked)}
                />
                Start immediately after creation
                {!detail.data.features.deployments && (
                  <span className="text-xs text-amber-600 dark:text-amber-400">
                    (plan does not include deployments)
                  </span>
                )}
              </label>

              {create.error != null && <ErrorNote error={create.error} />}

              <div className="flex justify-end border-t border-slate-100 pt-4 dark:border-slate-800">
                <Button
                  loading={create.isPending}
                  disabled={
                    !canSubmit ||
                    !subscription.coolifyProjectUuid ||
                    (mode === 'blueprint' &&
                      detail.data.features.services_blueprint_install !== true)
                  }
                  onClick={submit}
                >
                  {mode === 'blueprint' ? (
                    <>
                      <WandSparkles className="size-4" /> Create supported blueprint
                    </>
                  ) : mode === 'template' ? (
                    <>
                      <Rocket className="size-4" /> Create service
                    </>
                  ) : (
                    <>
                      <FileCode2 className="size-4" /> Create from compose
                    </>
                  )}
                </Button>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
